import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, map } from 'rxjs';

export interface HolidayItem {
  month: string;
  monthNumber: number;
  date: string;
  day: number;
  dayName: string;
  holidayName: string;
  hyderabad: boolean;
  gurugram: boolean;
  noida: boolean;
}

export interface HolidaySummaryItem {
  day: number;
  dayName: string;
  holidayName: string;
}

export interface CityHolidayList {
  count: number;
  dates: number[];
}

export interface YearHolidaysResponse {
  year: number;
  holidays: HolidayItem[];
}

export interface MonthHolidaysResponse {
  year: number;
  month: number;
  monthName: string;
  totalDays: number;
  summary: HolidaySummaryItem[];
  cityHolidays: {
    hyderabad: CityHolidayList;
    gurugram: CityHolidayList;
    noida: CityHolidayList;
  };
}

export interface ScheduleResponse {
  year: number;
  month: number;
  monthName: string;
  step1RangeStart: string;
  step1Deadline: string;
  step2RangeStart: string;
  step2RangeEnd: string;
  step2Deadline: string;
  step3RangeStart: string;
  step3Deadline: string;
  step4RangeStart: string;
  step4RangeEnd: string;
  step4Deadline: string;
}

interface ApiResponse<T> {
  status: string;
  reason?: string;
  data: T;
  message?: string;
}

export interface ResourceInfoRow {
  // Identity
  employeeId: string;
  resourceName: string;
  role?: string;
  email?: string;
  poiType?: string;
  employer?: string;
  city?: string;

  // Resource
  resourceId?: number;
  region?: string;

  // Assignment
  assignmentId?: number;
  hiringStatus?: string;
  reviewStatus?: string;
  dntIndiaLeader?: string;
  capexOpex?: string;
  poType?: string;
  locationType?: string;
  allocationPct?: number;
  billingStartDate?: string;
  billingEndDate?: string;

  // Project
  projectId?: number;
  projectName?: string;
  projectIdCode?: string;
  supportingLob?: string;
  businessSegment?: string;
  productSuite?: string;
  agileTeam?: string;
  svp?: string;
  vp?: string;
  functionalManager?: string;
  invoiceApprover?: string;
  glBusinessUnit?: string;
  billToFu?: string;
  billToMo?: string;

  // Rate Card
  tierModel?: string;
  dntCost?: number;
  fullHrAmount?: number;
  vendorRateCard?: string;
  rateCardMatch?: string;
  laptopIssued?: string;
  invoiceLocation?: string;
  reportingManager?: string;
  l4Manager?: string;
  glAccount?: string;
  doj?: string;

  // Derived from capex_opex
  activity?: string;
  category?: string;

  // Current-month FTE (joined from finance_monthly_fte at server-side
  // current date). Stored as a fraction (0.4 = 40%). Month/year echo back
  // so the FE renders the header label without computing "current month" itself.
  currentMonthFte?: number;
  currentMonthFteMonth?: number;
  currentMonthFteYear?: number;

  // Derived
  availableAllocationPct?: number;
  isNewHire: boolean;

  /**
   * Per-month metrics for the year (12 entries, index 0 = January).
   * Months outside the assignment's billing window have null fte / hours /
   * total. Empty / undefined for new-hire / unassigned rows.
   */
  months?: MonthMetrics[];
  /** Year the months[] array was computed for. */
  monthsYear?: number;

  /** Sum of every month's Total for the displayed year — Product's
   *  "TOTAL <year> $" column. Computed server-side from months[].total. */
  yearTotal?: number;

  /**
   * Set by the FE after fetch (not from the BE) when this row's employeeId
   * matches the prior row's. Used to render the multi-assignment grouping
   * pattern: hide Resource / History / Edit cells on continuation rows,
   * keep Copy to so users can copy each assignment independently.
   */
  isContinuation?: boolean;
}

/**
 * Per-month financial metrics rendered as 4 columns per month on the
 * Resource Info table (FTE / Hours / One Time / Total). Months outside
 * the assignment's billing window have null fte / hours / total.
 */
export interface MonthMetrics {
  fte?: number;
  hours?: number;
  oneTime?: number;
  total?: number;
}

export interface ResourceInfoPage {
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  rows: ResourceInfoRow[];
}

export interface ResourceInfoQuery {
  search?: string;

  // list-valued filters
  projectIds?: number[];
  supportingLobs?: string[];
  hiringStatuses?: string[];
  capexOpex?: string[];
  poTypes?: string[];
  tierModels?: string[];
  regions?: string[];
  locationTypes?: string[];
  invoiceLocations?: string[];
  poiTypes?: string[];
  laptopIssued?: string[];
  rateCardMatches?: string[];
  businessSegments?: string[];
  productSuites?: string[];
  agileTeams?: string[];
  employers?: string[];
  reviewStatuses?: string[];
  activities?: string[];
  categories?: string[];
  cities?: string[];
  reportingManagers?: string[];
  l4Managers?: string[];
  allocationBuckets?: number[];
  // Below: kept as optional strings (BE accepts both list + substring for now).
  // The modal renders them as dropdowns per Figma; the substring variant is
  // unused in the modal but the type stays open for future use.
  dntIndiaLeaders?: string[];
  svps?: string[];
  vps?: string[];
  functionalManagers?: string[];
  invoiceApprovers?: string[];

  // substring filters (kept as escape hatch; not surfaced in the modal)
  // reportingManager IS surfaced on the modal — Product flagged RM as
  // free-text manual entry rather than a dropdown.
  reportingManager?: string;
  dntIndiaLeader?: string;
  svp?: string;
  vp?: string;
  functionalManager?: string;
  invoiceApprover?: string;

  // flags
  needsReview?: boolean;
  newHiresOnly?: boolean;

  // sort — sortBy is a column key on the BE whitelist; sortDir is "asc"|"desc".
  // Both empty/undefined falls back to the BE's default 3-tier sort.
  sortBy?: string;
  sortDir?: string;

  page?: number;
  size?: number;

  /** Year used for the per-month columns (FTE / Hours / One Time / Total).
   *  Defaults to current year server-side if omitted. */
  year?: number;
}

export interface ReferenceEntry {
  code: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
}

/**
 * Row in the Finance Details / Resource Rate Card tab. One entry per
 * finance_rate_card_period record. Amount columns arrive decrypted from the
 * BE (endpoint is is_finance_admin-gated).
 */
export interface RateCardPeriodRow {
  rateCardId: number;

  resourceId: number;
  employeeId?: string;
  resourceName?: string;
  role?: string;
  employer?: string;

  assignmentId?: number;
  dntIndiaLeader?: string;

  projectId?: number;
  projectName?: string;
  projectIdCode?: string;
  supportingLob?: string;

  startDate?: string;
  endDate?: string;
  tierModel?: string;
  baseRate?: number;
  officeExpRecovery?: number;
  dntCost?: number;
  projectCost?: number;
  poType?: string;

  /** FE-derived continuation marker for blank-Resource visual grouping. */
  isContinuation?: boolean;
}

export interface RateCardPeriodPage {
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  rows: RateCardPeriodRow[];
}

export interface RateCardPeriodQuery {
  search?: string;
  projectIds?: number[];
  supportingLobs?: string[];
  sortBy?: string;
  sortDir?: string;
  page?: number;
  size?: number;
}

/**
 * Row in the Finance Details / Project Details table reached via the
 * View Projects button. One entry per finance_project record. Fields
 * past functionalManager don't render in the Figma's table columns
 * but are returned so the Edit modal can pre-populate every input.
 */
export interface ProjectDetailsRow {
  projectId: number;
  projectIdCode?: string;
  projectName?: string;
  status?: string;
  businessSegment?: string;
  supportingLob?: string;
  productSuite?: string;
  agileTeam?: string;
  svp?: string;
  vp?: string;
  functionalManager?: string;
  invoiceApprover?: string;
  glBusinessUnit?: string;
  billToFu?: string;
  billToMo?: string;
}

/**
 * Distinct values from existing finance_project rows for the free-text
 * autocomplete fields in the Add / Edit modal. Business Segment +
 * Supporting LOB come from canonical reference tables instead.
 */
export interface ProjectAutocompleteSuggestions {
  productSuites: string[];
  agileTeams: string[];
  svps: string[];
  vps: string[];
  functionalManagers: string[];
  invoiceApprovers: string[];
}

/**
 * Distinct values harvested from vendor_management.employee, used to
 * populate the Filter by modal's employee-driven dropdowns (D&T India
 * Leader, L4 Manager, City). Reporting Manager is intentionally absent —
 * Product flagged it as free-text manual entry, not a dropdown.
 */
export interface EmployeeDistinctValues {
  l3Names: string[];
  l4Names: string[];
  cities: string[];
}

export interface ProjectDetailsPage {
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  rows: ProjectDetailsRow[];
}

export interface ProjectDetailsQuery {
  search?: string;
  businessSegments?: string[];
  supportingLobs?: string[];
  statuses?: string[];
  sortBy?: string;
  sortDir?: string;
  page?: number;
  size?: number;
}

/**
 * Payload for POST /api/v1/finance/resource/new-hire/{employeeId} —
 * submitted by the New Hire Form modal on Resource Info when the
 * operator clicks Edit on a row whose Hiring Status is "New Hire".
 * Project-derived attributes (Product Suite, Agile Team, D&T Invoice
 * Approver, Activity, Category) aren't sent — they're pulled from the
 * picked project / capex_opex code server-side.
 */
export interface NewHireProvisionRequest {
  projectId: number;
  hiringStatus: string;
  capexOpex?: string;
  allocationPct?: number;
  poType?: string;
  billingStartDate: string;
  billingEndDate?: string;
  tierModel?: string;
  dntCost?: number;
  fullHrAmount?: number;
  vendorRateCard?: string;
  rateCardMatch?: string;
  glBusinessUnit?: string;
  billToFu?: string;
  billToMo?: string;
  glAccount?: string;
  invoiceLocation?: string;
}

export interface NewHireProvisionResult {
  resourceId: number;
  assignmentId: number;
  rateCardId?: number;
}

/**
 * Distinct values from existing finance_rate_card_period rows for the
 * free-text autocomplete fields on the New Hire Form modal.
 */
export interface RateCardAutocompleteSuggestions {
  vendorRateCards: string[];
  glBusinessUnits: string[];
  billToFus: string[];
  billToMos: string[];
  glAccounts: string[];
  invoiceLocations: string[];
}

/**
 * Payload for POST /api/v1/finance/project-details — submitted by the
 * Add Projects modal on the Project Details page. Status defaults to
 * "Active" server-side; the modal doesn't expose it.
 */
export interface AddProjectRequest {
  projectName: string;
  projectIdCode: string;
  businessSegment?: string;
  supportingLob?: string;
  productSuite?: string;
  agileTeam?: string;
  svp?: string;
  vp?: string;
  functionalManager?: string;
  invoiceApprover?: string;
  glBusinessUnit?: string;
  billToFu?: string;
  billToMo?: string;
}

/**
 * Payload for PUT /api/v1/finance/project-details/{id} — submitted by the
 * Edit Project modal. Same shape as AddProjectRequest plus an editable
 * status (Active / Inactive) which the Add flow doesn't expose.
 */
export interface UpdateProjectRequest extends AddProjectRequest {
  status: 'Active' | 'Inactive';
}

/**
 * One project in the Finance Details picker. Lightweight shape — only
 * the fields the pickers render or derive from. Returned by
 * GET /finance/projects, used by the Edit Project page (Project Name &
 * ID picker), New Project tab, and Copy Resource Data flow.
 */
export interface FinanceProjectOption {
  id: number;
  projectName?: string;
  projectIdCode?: string;
  supportingLob?: string;
  status?: string;
  /** GL Business Unit on the project — "Bill to BU" on the Edit
   *  Project form. Auto-fills the section's read-only field on
   *  project pick. */
  glBusinessUnit?: string;
  /** Auto-fills the section's read-only Bill to FU on project pick. */
  billToFu?: string;
  /** Auto-fills the section's read-only Bill to MO on project pick. */
  billToMo?: string;
}

/**
 * One editable assignment-row on the Edit Project page. Mirrors the BE's
 * EditResourceProjectView.ProjectSection shape field-for-field so the
 * form can map straight through on load + save.
 */
export interface EditResourceProjectSection {
  assignmentId: number;
  projectId?: number;
  projectName?: string;
  projectIdCode?: string;
  /** Composed "{Leader} ({LOB})" — read-only on the form. */
  /** D&T India Leader — assignment-scoped; doesn't change on project pick. */
  dntIndiaLeader?: string;
  /** Supporting LOB — project-scoped; auto-updates when picker changes. */
  supportingLob?: string;
  hiringStatus?: string;
  allocationPct?: number;
  billingStartDate?: string;
  billingEndDate?: string;
  rateCardId?: number;
  baseRate?: number;
  billToFu?: string;
  billToMo?: string;
  billToBu?: string;
  /** Tier Model on the rate card. Editable on the Current Project tab;
   *  drives the monthly Total formula via finance_tier_rate. */
  tierModel?: string;
  /** Free-text rate-card sanity-check value. Editable. */
  vendorRateCard?: string;
  /** Dropdown value (Dell / Lenovo / MacBook / MacBook Pro / VDI / None). */
  laptopIssued?: string;
  /** Auto-derived from capex/opex on save (CAPEX → 162800, else → 577003).
   *  Read-only on the form — surfaced so the operator can see the value. */
  glAccount?: string;
}

export interface EditResourceProjectView {
  resourceId: number;
  resourceName: string;
  employeeId: string;
  /** "EMP" / "CWR" from Employee. Gates Tier Model auto-fill in the
   *  Edit Project form — only EMP rows on Tier 1-4 should patch Base
   *  Rate from the master; CWR + Actuals are manual entry. */
  poiType?: string;
  projects: EditResourceProjectSection[];
}

/** Save payload for the Edit Project page — array of edited sections. */
export interface EditResourceProjectsRequest {
  projects: EditResourceProjectSection[];
}

export interface EditResourceProjectsResult {
  assignmentsUpdated: number;
  rateCardsUpdated: number;
}

/** Counts returned by the Copy Resource Data flow. */
export interface CopyResourceProjectsResult {
  assignmentsCopied: number;
  rateCardsCopied: number;
  /** Source assignments skipped because the target already had a row
   *  at the same (project, billing start date) key. */
  assignmentsSkipped: number;
}

/**
 * Payload for the New Project tab — adds one fresh assignment (and
 * optionally a paired rate-card period) to a resource. Maps to the BE
 * AddResourceProjectRequest field-for-field.
 */
/**
 * One row in the Edit Project &rarr; One Time tab list. Read-only view
 * of a posted one-time cost adjustment for a resource. Amount arrives
 * plaintext — the BE entity's converter decrypts on the way out.
 */
export interface OneTimeEntryView {
  id: number;
  targetYear: number;
  targetMonth: number;
  amount: number;
  comments: string;
  createdBy: string;
  createdAt: string;
}

/**
 * Per-assignment per-month FTE allocation row. Editable on the
 * Edit Project / One Time tab's Monthly FTE editor — operator
 * sets a 0.0-1.0 decimal per month per assignment.
 *
 * <p>{@code fteValue} null on the write side deletes the row so
 * an operator can clear a month they previously set.
 */
export interface MonthlyFteEntry {
  assignmentId: number;
  targetYear: number;
  targetMonth: number;
  fteValue: number | null;
  projectName?: string;
  projectIdCode?: string;
}

/** Payload for posting a new one-time entry. */
export interface AddOneTimeEntryRequest {
  targetYear: number;
  /** 1-12. */
  targetMonth: number;
  amount: number;
  comments: string;
}

/**
 * Master tier-rate row keyed by (tierModel, targetYear). Returned by the
 * /finance/tier-rates endpoint. Powers the Edit Project Tier Model
 * auto-fill (pick a tier → Base Rate prefills from this master).
 */
export interface TierRateRow {
  id: number;
  tierModel: string;
  targetYear: number;
  baseRate?: number;
  officeExpRecovery?: number;
}

/** Payload for POST /finance/tier-rates. */
export interface AddTierRateRequest {
  tierModel: string;
  targetYear: number;
  baseRate?: number;
  officeExpRecovery?: number;
}

/** Payload for PUT /finance/tier-rates/{id}. Same shape as Add — Update lets
 *  the operator rename the tier or move it to a different year. */
export type UpdateTierRateRequest = AddTierRateRequest;

export interface AddResourceProjectRequest {
  projectId: number;
  hiringStatus: string;
  /** CAPEX / PROJ_OPEX / RTB_OPEX — required by the DB constraint. */
  capexOpex: string;
  allocationPct?: number;
  billingStartDate: string;
  billingEndDate?: string;
  dntIndiaLeader?: string;
  baseRate?: number;
  billToFu?: string;
  billToMo?: string;
  billToBu?: string;
  /** Tier Model on the rate card — drives the monthly Total formula. */
  tierModel?: string;
}

/**
 * One row in the Resource History side panel. Wraps either an assignment-
 * history or a rate-card-history snapshot in a unified shape — the FE
 * doesn't branch on entityKind for rendering.
 */
export interface ResourceHistoryEntry {
  entityKind: 'assignment' | 'rate_card';
  historyId: number;
  entityId?: number;
  projectId?: number;
  projectName?: string;
  projectIdCode?: string;
  changeType: 'CREATE' | 'UPDATE' | 'DELETE';
  changedBy: string;
  changedAt: string;
  snapshot: Record<string, unknown>;
  /**
   * Field-level diffs vs. the immediately-prior snapshot for the same
   * entity. Populated by the BE for the Recent Activities feed; null on
   * the very first event for an entity (no prior snapshot to diff).
   */
  changes?: HistoryChange[];
}

export interface HistoryChange {
  field: string;
  from?: unknown;
  to?: unknown;
}

export interface MigrationReportRowError {
  sourceRowNumber: number;
  employeeId?: string;
  reason: string;
}

export interface MigrationReportRowWarning {
  sourceRowNumber: number;
  employeeId?: string;
  reason: string;
}

export interface MigrationReport {
  totalRowsRead: number;
  imported: number;
  skippedIdempotent: number;
  errors: number;
  warnings: number;
  projectsCreated: number;
  resourcesCreated: number;
  referenceValuesCreated: number;
  rowErrors: MigrationReportRowError[];
  rowWarnings: MigrationReportRowWarning[];
}

export type MigrationJobStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface MigrationJob {
  jobId: string;
  filename: string;
  fileSizeBytes: number;
  submittedBy: string;
  submittedAt: string;
  status: MigrationJobStatus;
  startedAt?: string;
  completedAt?: string;
  totalRowsRead: number;
  processed: number;
  imported: number;
  skipped: number;
  errors: number;
  warnings: number;
  projectsCreated: number;
  resourcesCreated: number;
  referenceValuesCreated: number;
  oneTimeEntriesInserted: number;
  report?: MigrationReport;
  errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class FinanceService {
  private readonly baseUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getHolidaysForYear(year: number): Observable<YearHolidaysResponse> {
    return this.http
      .get<ApiResponse<YearHolidaysResponse>>(`${this.baseUrl}/finance/holidays`, { params: { year } })
      .pipe(map((r) => r.data));
  }

  getHolidaysForMonth(year: number, month: number): Observable<MonthHolidaysResponse> {
    return this.http
      .get<ApiResponse<MonthHolidaysResponse>>(`${this.baseUrl}/finance/holidays/month`, {
        params: { year, month },
      })
      .pipe(map((r) => r.data));
  }

  getSchedule(year: number, month: number): Observable<ScheduleResponse> {
    return this.http
      .get<ApiResponse<ScheduleResponse>>(`${this.baseUrl}/finance/recharge-recovery/schedule`, {
        params: { year, month },
      })
      .pipe(map((r) => r.data));
  }

  getRecipients(): Observable<{ defaultTo: string[]; defaultCc: string[] }> {
    return this.http
      .get<ApiResponse<{ defaultToRecipients: string[]; defaultCcRecipients: string[] }>>(
        `${this.baseUrl}/finance/recharge-recovery/recipients`,
      )
      .pipe(
        map((r) => ({
          defaultTo: r.data?.defaultToRecipients ?? [],
          defaultCc: r.data?.defaultCcRecipients ?? [],
        })),
      );
  }

  sendRechargeRecoveryEmail(payload: {
    year: number;
    month: number;
    toRecipients: string[];
    ccRecipients: string[];
    additionalEmails: string[];
  }): Observable<{ logId: number; status: string; sentAt: string; message: string }> {
    return this.http
      .post<ApiResponse<{ logId: number; status: string; sentAt: string; message: string }>>(
        `${this.baseUrl}/finance/recharge-recovery/send`,
        payload,
      )
      .pipe(map((r) => r.data));
  }

  listResourceInfo(query: ResourceInfoQuery): Observable<ResourceInfoPage> {
    let params = new HttpParams();
    // Iterate every field on the query object; the BE @ModelAttribute binding
    // matches by name, so we just need to send name=value (or repeated
    // name=value pairs for list fields). Skipping empty / null entries
    // keeps the URL tidy and the WHERE clause noop-clean.
    const q = query as Record<string, unknown>;
    for (const key of Object.keys(q)) {
      const value = q[key];
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        value.forEach((v) => {
          params = params.append(key, String(v));
        });
      } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) params = params.set(key, trimmed);
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        params = params.set(key, String(value));
      }
    }
    if (!params.has('page')) params = params.set('page', '0');
    if (!params.has('size')) params = params.set('size', '100');
    return this.http
      .get<ApiResponse<ResourceInfoPage>>(`${this.baseUrl}/finance/resource-info`, { params })
      .pipe(map((r) => r.data));
  }

  /**
   * Loads the Resource Rate Card table. Same shape as listResourceInfo —
   * the query object's keys map 1:1 to BE @ModelAttribute fields and list
   * values produce repeated query-string params. The endpoint requires
   * isFinanceAdmin; non-admin callers receive 403.
   */
  listRateCardPeriods(query: RateCardPeriodQuery): Observable<RateCardPeriodPage> {
    let params = new HttpParams();
    const q = query as Record<string, unknown>;
    for (const key of Object.keys(q)) {
      const value = q[key];
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        value.forEach((v) => {
          params = params.append(key, String(v));
        });
      } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) params = params.set(key, trimmed);
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        params = params.set(key, String(value));
      }
    }
    if (!params.has('page')) params = params.set('page', '0');
    if (!params.has('size')) params = params.set('size', '100');
    return this.http
      .get<ApiResponse<RateCardPeriodPage>>(`${this.baseUrl}/finance/rate-card-periods`, { params })
      .pipe(map((r) => r.data));
  }

  /**
   * Provisions a new-hire's first assignment + rate card via the New Hire
   * Form modal on Resource Info. employeeId is the HR id (Employee
   * .employee_id). The BE creates a FinanceResource on the fly when the
   * employee doesn't have one yet.
   */
  provisionNewHire(
    employeeId: string,
    payload: NewHireProvisionRequest,
  ): Observable<NewHireProvisionResult> {
    return this.http
      .post<ApiResponse<NewHireProvisionResult>>(
        `${this.baseUrl}/finance/resource/new-hire/${employeeId}`,
        payload,
      )
      .pipe(map((r) => r.data));
  }

  /**
   * Distinct existing values from finance_rate_card_period for the free-
   * text autocomplete inputs on the New Hire Form modal.
   */
  getRateCardAutocompleteSuggestions(): Observable<RateCardAutocompleteSuggestions> {
    return this.http
      .get<ApiResponse<RateCardAutocompleteSuggestions>>(
        `${this.baseUrl}/finance/resource/new-hire/autocomplete-suggestions`,
      )
      .pipe(
        map(
          (r) =>
            r.data ?? {
              vendorRateCards: [],
              glBusinessUnits: [],
              billToFus: [],
              billToMos: [],
              glAccounts: [],
              invoiceLocations: [],
            },
        ),
      );
  }

  /**
   * Loads the Project Details table reached via View Projects. Same query
   * shape pattern as listResourceInfo / listRateCardPeriods.
   */
  listProjectDetails(query: ProjectDetailsQuery): Observable<ProjectDetailsPage> {
    let params = new HttpParams();
    const q = query as Record<string, unknown>;
    for (const key of Object.keys(q)) {
      const value = q[key];
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        value.forEach((v) => {
          params = params.append(key, String(v));
        });
      } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) params = params.set(key, trimmed);
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        params = params.set(key, String(value));
      }
    }
    if (!params.has('page')) params = params.set('page', '0');
    if (!params.has('size')) params = params.set('size', '100');
    return this.http
      .get<ApiResponse<ProjectDetailsPage>>(`${this.baseUrl}/finance/project-details`, { params })
      .pipe(map((r) => r.data));
  }

  /**
   * Creates a finance_project row from the Add Projects modal. Returns the
   * newly-created project as a ProjectDetailsRow so the FE can prepend it
   * to the table without an extra fetch. BE is admin-gated — non-admins
   * receive 403.
   */
  createProject(payload: AddProjectRequest): Observable<ProjectDetailsRow> {
    return this.http
      .post<ApiResponse<ProjectDetailsRow>>(
        `${this.baseUrl}/finance/project-details`,
        payload,
      )
      .pipe(map((r) => r.data));
  }

  /**
   * Updates an existing finance_project row. Same admin-gating + same
   * duplicate-id error semantics as createProject. Returns the updated
   * row so the FE can splice it into the table without an extra fetch.
   */
  updateProject(
    id: number,
    payload: UpdateProjectRequest,
  ): Observable<ProjectDetailsRow> {
    return this.http
      .put<ApiResponse<ProjectDetailsRow>>(
        `${this.baseUrl}/finance/project-details/${id}`,
        payload,
      )
      .pipe(map((r) => r.data));
  }

  /**
   * Loads distinct existing values from finance_project for the free-text
   * autocomplete fields in the Add / Edit Project modal. One round-trip,
   * loaded eagerly when the Project Details page opens.
   */
  getProjectAutocompleteSuggestions(): Observable<ProjectAutocompleteSuggestions> {
    return this.http
      .get<ApiResponse<ProjectAutocompleteSuggestions>>(
        `${this.baseUrl}/finance/project-details/autocomplete-suggestions`,
      )
      .pipe(
        map(
          (r) =>
            r.data ?? {
              productSuites: [],
              agileTeams: [],
              svps: [],
              vps: [],
              functionalManagers: [],
              invoiceApprovers: [],
            },
        ),
      );
  }

  listReferenceEntries(tableSlug: string): Observable<ReferenceEntry[]> {
    return this.http
      .get<ApiResponse<ReferenceEntry[]>>(`${this.baseUrl}/finance/reference/${tableSlug}`)
      .pipe(map((r) => r.data ?? []));
  }

  /**
   * Distinct Employee-side values for the Filter by modal (L3 = D&T India
   * Leader, L4 = L4 Manager, City). One round-trip populates three filter
   * dropdowns whose values aren't backed by a finance reference table.
   */
  getEmployeeDistinctValues(): Observable<EmployeeDistinctValues> {
    return this.http
      .get<ApiResponse<EmployeeDistinctValues>>(`${this.baseUrl}/finance/resource-info/employee-distinct-values`)
      .pipe(map((r) => r.data ?? { l3Names: [], l4Names: [], cities: [] }));
  }

  listProjectHistory(resourceId: number,
                     startDate?: string,
                     endDate?: string): Observable<ResourceHistoryEntry[]> {
    let params = new HttpParams().set('resourceId', String(resourceId));
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http
      .get<ApiResponse<ResourceHistoryEntry[]>>(`${this.baseUrl}/finance/resource-history`, { params })
      .pipe(map((r) => r.data ?? []));
  }

  listRecentActivities(resourceId: number,
                       startDate?: string,
                       endDate?: string): Observable<ResourceHistoryEntry[]> {
    let params = new HttpParams().set('resourceId', String(resourceId));
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http
      .get<ApiResponse<ResourceHistoryEntry[]>>(`${this.baseUrl}/finance/resource-history/recent`, { params })
      .pipe(map((r) => r.data ?? []));
  }

  listFinanceProjects(): Observable<FinanceProjectOption[]> {
    return this.http
      .get<ApiResponse<FinanceProjectOption[]>>(`${this.baseUrl}/finance/projects`)
      .pipe(map((r) => r.data ?? []));
  }

  /**
   * Loads master tier rates for the requested year. Used by Edit Project
   * to auto-fill Base Rate when the operator picks a Tier Model on the
   * Current Project / New Project forms. Finance-admin only on the BE.
   */
  listTierRates(year: number): Observable<TierRateRow[]> {
    const params = new HttpParams().set('year', String(year));
    return this.http
      .get<ApiResponse<TierRateRow[]>>(`${this.baseUrl}/finance/tier-rates`, { params })
      .pipe(map((r) => r.data ?? []));
  }

  createTierRate(request: AddTierRateRequest): Observable<TierRateRow> {
    return this.http
      .post<ApiResponse<TierRateRow>>(`${this.baseUrl}/finance/tier-rates`, request)
      .pipe(map((r) => r.data));
  }

  updateTierRate(id: number, request: UpdateTierRateRequest): Observable<TierRateRow> {
    return this.http
      .put<ApiResponse<TierRateRow>>(`${this.baseUrl}/finance/tier-rates/${id}`, request)
      .pipe(map((r) => r.data));
  }

  deleteTierRate(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse<void>>(`${this.baseUrl}/finance/tier-rates/${id}`)
      .pipe(map(() => undefined));
  }

  loadEditProjectView(resourceId: number): Observable<EditResourceProjectView> {
    return this.http
      .get<ApiResponse<EditResourceProjectView>>(`${this.baseUrl}/finance/resource/${resourceId}/projects`)
      .pipe(map((r) => r.data));
  }

  saveEditProjects(resourceId: number, request: EditResourceProjectsRequest):
    Observable<EditResourceProjectsResult> {
    return this.http
      .put<ApiResponse<EditResourceProjectsResult>>(
        `${this.baseUrl}/finance/resource/${resourceId}/projects`,
        request,
      )
      .pipe(map((r) => r.data));
  }

  addResourceProject(resourceId: number, request: AddResourceProjectRequest): Observable<number> {
    return this.http
      .post<ApiResponse<number>>(
        `${this.baseUrl}/finance/resource/${resourceId}/projects`,
        request,
      )
      .pipe(map((r) => r.data));
  }

  copyResourceProjects(sourceResourceId: number, targetResourceId: number):
    Observable<CopyResourceProjectsResult> {
    return this.http
      .post<ApiResponse<CopyResourceProjectsResult>>(
        `${this.baseUrl}/finance/resource/${sourceResourceId}/copy-to/${targetResourceId}`,
        {},
      )
      .pipe(map((r) => r.data));
  }

  /** Per-assignment copy used by the Copy Resource Data modal on a
   *  single Resource Info row. */
  copyAssignmentTo(sourceAssignmentId: number, targetResourceId: number):
    Observable<CopyResourceProjectsResult> {
    return this.http
      .post<ApiResponse<CopyResourceProjectsResult>>(
        `${this.baseUrl}/finance/resource/assignment/${sourceAssignmentId}/copy-to/${targetResourceId}`,
        {},
      )
      .pipe(map((r) => r.data));
  }

  /** Per-assignment copy that targets by Employee.employeeId. If the
   *  employee has no FinanceResource row yet, the BE provisions one
   *  on the fly before completing the copy — lets new-hire employees
   *  be valid copy targets without a separate provisioning workflow. */
  copyAssignmentToEmployee(sourceAssignmentId: number, targetEmployeeId: string):
    Observable<CopyResourceProjectsResult> {
    return this.http
      .post<ApiResponse<CopyResourceProjectsResult>>(
        `${this.baseUrl}/finance/resource/assignment/${sourceAssignmentId}/copy-to-employee/${encodeURIComponent(targetEmployeeId)}`,
        {},
      )
      .pipe(map((r) => r.data));
  }

  listOneTimeEntries(resourceId: number): Observable<OneTimeEntryView[]> {
    return this.http
      .get<ApiResponse<OneTimeEntryView[]>>(
        `${this.baseUrl}/finance/resource/${resourceId}/one-time`,
      )
      .pipe(map((r) => r.data ?? []));
  }

  addOneTimeEntry(resourceId: number, request: AddOneTimeEntryRequest): Observable<number> {
    return this.http
      .post<ApiResponse<number>>(
        `${this.baseUrl}/finance/resource/${resourceId}/one-time`,
        request,
      )
      .pipe(map((r) => r.data));
  }

  listMonthlyFte(resourceId: number, year: number): Observable<MonthlyFteEntry[]> {
    const params = new HttpParams().set('year', String(year));
    return this.http
      .get<ApiResponse<MonthlyFteEntry[]>>(
        `${this.baseUrl}/finance/resource/${resourceId}/monthly-fte`,
        { params },
      )
      .pipe(map((r) => r.data ?? []));
  }

  upsertMonthlyFte(resourceId: number, entries: MonthlyFteEntry[]): Observable<number> {
    return this.http
      .put<ApiResponse<number>>(
        `${this.baseUrl}/finance/resource/${resourceId}/monthly-fte`,
        { entries },
      )
      .pipe(map((r) => r.data ?? 0));
  }

  startMigration(file: File): Observable<MigrationJob> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http
      .post<ApiResponse<MigrationJob>>(`${this.baseUrl}/finance/admin/migration-upload`, form)
      .pipe(map((r) => r.data));
  }

  getMigrationStatus(jobId: string): Observable<MigrationJob> {
    return this.http
      .get<ApiResponse<MigrationJob>>(`${this.baseUrl}/finance/admin/migration-status`, {
        params: { jobId },
      })
      .pipe(map((r) => r.data));
  }
}
